import { getDealValidity } from "@/lib/deals/dealValidity";
import { ExternalLink, AlertTriangle, Users, ShoppingBag, Ban, Calendar } from "lucide-react";
import { 
  type Deal, 
  inferDealCategory, 
  computeDealValueCad, 
  computeEffectivePercentBack,
  formatInKindTiers,
  hasValuation,
  needsConfirmOnSite,
  formatDiscountPercent,
  formatSpendAmount,
  formatDiscountValue,
  computeDiscountDollarValue
} from "@/lib/types/deals";
import { formatEligibilityRange } from "@/lib/deals/formatEligibilityRange";

interface DetailLayoutProps {
  deal: Deal;
}

// =============================================================================
// ELIGIBILITY SECTION - Shared component for showing membership, products, etc.
// =============================================================================

interface EligibilityInfo {
  membership?: string | null;
  includedProducts?: string | null;
  excludedProducts?: string | null;
  minSpend?: number | null;
  validFrom?: string | null;
  validUntil?: string | null;
}

/**
 * Extract eligibility info from deal fields (membership, exclusions, fine_print)
 */
function extractEligibilityInfo(deal: Deal): EligibilityInfo {
  const info: EligibilityInfo = {};
  
  // Membership requirement
  if (deal.access_requirement_program_name) {
    info.membership = deal.access_requirement_program_name;
  } else if (deal.access_requirement_notes) {
    info.membership = deal.access_requirement_notes;
  }
  
  // Min spend (qualifying_spend_cad)
  info.minSpend = deal.qualifying_spend_cad ?? deal.spend_amount ?? null;
  
  // Exclusions from deal.exclusions field
  if (deal.exclusions) {
    info.excludedProducts = deal.exclusions;
  }
  
  // Try to extract included products from fine_print if it mentions specific products
  // Look for patterns like "on PAX, BOAXEL" or "for select wardrobes"
  if (deal.fine_print) {
    const finePrint = deal.fine_print;
    
    // Check for "on [products]" or "for [products]" patterns
    const includeMatch = finePrint.match(/(?:on|for|includes?)\s+([A-Z][A-Z0-9,\s&]+(?:systems?|wardrobes?|products?)?)/i);
    if (includeMatch && !includeMatch[1].toLowerCase().includes('exclud')) {
      info.includedProducts = includeMatch[1].trim();
    }
  }
  
  // CR432: Use unified validity helper instead of direct field access
  const validity = getDealValidity(deal);
  info.validFrom = validity.validFrom ?? deal.purchase_window_start ?? null;
  info.validUntil = validity.validTo ?? deal.purchase_window_end ?? null;
  
  return info;
}

// CR282: Removed local formatDateRange - using centralized formatEligibilityRange instead

/**
 * Eligibility Section Component
 */
function EligibilitySection({ deal }: { deal: Deal }) {
  const info = extractEligibilityInfo(deal);
  
  // Only render if we have any eligibility info
  const hasAnyInfo = info.membership || info.includedProducts || info.excludedProducts || 
                      (info.validFrom || info.validUntil);
  
  if (!hasAnyInfo) return null;
  
  const dateRange = formatEligibilityRange(info.validFrom, info.validUntil);
  
  return (
    <div className="bg-muted/30 rounded-lg p-3 space-y-1.5 border border-border/50">
      <p className="text-xs font-semibold text-foreground mb-2">Eligibility</p>
      
      {info.membership && (
        <div className="flex items-start gap-2 text-xs">
          <Users className="w-3.5 h-3.5 text-muted-foreground mt-0.5 flex-shrink-0" />
          <div>
            <span className="text-muted-foreground">Who: </span>
            <span className="text-foreground">{info.membership} members only</span>
          </div>
        </div>
      )}
      
      {info.includedProducts && (
        <div className="flex items-start gap-2 text-xs">
          <ShoppingBag className="w-3.5 h-3.5 text-muted-foreground mt-0.5 flex-shrink-0" />
          <div>
            <span className="text-muted-foreground">What to buy: </span>
            <span className="text-foreground">{info.includedProducts}</span>
          </div>
        </div>
      )}
      
      {info.excludedProducts && (
        <div className="flex items-start gap-2 text-xs">
          <Ban className="w-3.5 h-3.5 text-muted-foreground mt-0.5 flex-shrink-0" />
          <div>
            <span className="text-muted-foreground">Excludes: </span>
            <span className="text-foreground">{info.excludedProducts}</span>
          </div>
        </div>
      )}
      
      {dateRange && (
        <div className="flex items-start gap-2 text-xs">
          <Calendar className="w-3.5 h-3.5 text-muted-foreground mt-0.5 flex-shrink-0" />
          <div>
            <span className="text-muted-foreground">Valid: </span>
            <span className="text-foreground">{dateRange}</span>
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// BONUS PERCENT BACK LAYOUT - For "X% back in gift card" with min spend
// =============================================================================

/**
 * Check if this is a "% back on gift card" BONUS deal
 */
export function isBonusPercentBackDeal(deal: Deal): boolean {
  const category = inferDealCategory(deal);
  return category === "BONUS" && 
         deal.discount_percent != null && 
         deal.discount_percent > 0 &&
         (deal.qualifying_spend_cad != null || deal.spend_amount != null);
}

/**
 * Bonus Percent Back Layout - "Spend $X, get $Y in gift cards" deals
 */
export function BonusPercentBackDetailLayout({ deal }: DetailLayoutProps) {
  const spend = deal.qualifying_spend_cad ?? deal.spend_amount ?? 0;
  const percent = deal.discount_percent ?? 0;
  const displayBonusValue = Math.round(spend * (percent / 100));
  const effectivePercent = computeEffectivePercentBack(deal);
  
  return (
    <div className="space-y-3 text-sm">
      {/* Headline example */}
      <div className="bg-green-50 dark:bg-green-950/30 rounded-lg p-4 border border-green-200 dark:border-green-800">
        <p className="text-green-800 dark:text-green-200 font-bold text-lg">
          Spend ${spend.toLocaleString()}, get ${displayBonusValue.toLocaleString()} in gift cards
        </p>
        <p className="text-green-700 dark:text-green-400 text-sm mt-1">
          {percent}% back in a digital gift card
        </p>
      </div>
      
      {/* Value blocks */}
      <div className="grid grid-cols-2 gap-3">
        <div className="bg-muted/50 rounded-lg p-3">
          <p className="text-muted-foreground text-xs mb-1">Qualifying Spend</p>
          <p className="font-semibold text-foreground">${spend.toLocaleString()}</p>
        </div>
        <div className="bg-green-50 dark:bg-green-950/30 rounded-lg p-3">
          <p className="text-green-700 dark:text-green-400 text-xs mb-1">Reward</p>
          <p className="font-semibold text-green-800 dark:text-green-300">${displayBonusValue.toLocaleString()}</p>
        </div>
      </div>
      
      {/* Effective value */}
      {effectivePercent != null && (
        <div className="bg-primary/10 rounded-lg p-3">
          <p className="text-primary text-xs mb-1">Effective Value</p>
          <p className="font-bold text-primary text-lg">≈ {Math.round(effectivePercent)}% back</p>
          <p className="text-primary/70 text-xs">≈ ${displayBonusValue.toLocaleString()} value</p>
        </div>
      )}
      
      {/* Eligibility section */}
      <EligibilitySection deal={deal} />
    </div>
  );
}

// =============================================================================
// STANDARD BONUS LAYOUT - Cash back or bonus card deals (fixed amounts)
// =============================================================================

/**
 * Bonus Layout - Cash back or bonus card deals
 */
export function BonusDetailLayout({ deal }: DetailLayoutProps) {
  // Check if this is a "% back" bonus deal
  if (isBonusPercentBackDeal(deal)) {
    return <BonusPercentBackDetailLayout deal={deal} />;
  }
  
  const spend = deal.qualifying_spend_cad ?? deal.spend_amount;
  const bonus = deal.bonus_value_cad ?? deal.bonus_amount ?? deal.bonus_or_discount_amount;
  const bonusTypeLabel = deal.bonus_type === "CASH_BACK" ? "Cash Back" : "Bonus Card";
  const effectivePercent = computeEffectivePercentBack(deal);
  
  return (
    <div className="space-y-3 text-sm">
      <div className="grid grid-cols-2 gap-3">
        {spend && (
          <div className="bg-muted/50 rounded-lg p-3">
            <p className="text-muted-foreground text-xs mb-1">Qualifying Spend</p>
            <p className="font-semibold text-foreground">${spend.toLocaleString()}</p>
          </div>
        )}
        {bonus && (
          <div className="bg-green-50 dark:bg-green-950/30 rounded-lg p-3">
            <p className="text-green-700 dark:text-green-400 text-xs mb-1">Reward ({bonusTypeLabel})</p>
            <p className="font-semibold text-green-800 dark:text-green-300">${bonus.toLocaleString()}</p>
          </div>
        )}
      </div>
      
      {effectivePercent != null && (
        <div className="bg-primary/10 rounded-lg p-3">
          <p className="text-primary text-xs mb-1">Effective Value</p>
          <p className="font-bold text-primary text-lg">≈ {Math.round(effectivePercent)}% back</p>
          {bonus && <p className="text-primary/70 text-xs">≈ ${bonus} value</p>}
        </div>
      )}
      
      {/* Eligibility section for standard bonus deals too */}
      <EligibilitySection deal={deal} />
    </div>
  );
}

/**
 * Discount Layout - Pay X Get Y, % off, $ off
 * Updated to use proper formatting with rounding rules
 */
export function DiscountDetailLayout({ deal }: DetailLayoutProps) {
  const dollarValue = computeDiscountDollarValue(deal);
  const effectivePercent = deal.discount_percent ?? computeEffectivePercentBack(deal);
  const spend = deal.qualifying_spend_cad ?? deal.min_purchase_cad ?? deal.spend_amount;
  
  return (
    <div className="space-y-3 text-sm">
      {/* Pay X Get Y */}
      {deal.discount_subtype === "PAY_X_GET_Y_VALUE" && deal.pay_amount_cad && deal.receive_value_cad && (
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-muted/50 rounded-lg p-3">
            <p className="text-muted-foreground text-xs mb-1">You Pay</p>
            <p className="font-semibold text-foreground">{formatSpendAmount(deal.pay_amount_cad)}</p>
          </div>
          <div className="bg-blue-50 dark:bg-blue-950/30 rounded-lg p-3">
            <p className="text-blue-700 dark:text-blue-400 text-xs mb-1">You Get</p>
            <p className="font-semibold text-blue-800 dark:text-blue-300">{formatSpendAmount(deal.receive_value_cad)} value</p>
          </div>
        </div>
      )}
      
      {/* Percent Off - with qualifying spend */}
      {deal.discount_subtype === "PERCENT_OFF_PURCHASE" && deal.discount_percent && (
        <div className="grid grid-cols-2 gap-3">
          {spend && (
            <div className="bg-muted/50 rounded-lg p-3">
              <p className="text-muted-foreground text-xs mb-1">Qualifying Spend</p>
              <p className="font-semibold text-foreground">{formatSpendAmount(spend)}</p>
            </div>
          )}
          <div className="bg-blue-50 dark:bg-blue-950/30 rounded-lg p-3">
            <p className="text-blue-700 dark:text-blue-400 text-xs mb-1">Discount</p>
            <p className="font-bold text-blue-800 dark:text-blue-300 text-lg">{formatDiscountPercent(deal.discount_percent)} off</p>
          </div>
        </div>
      )}
      
      {/* Dollar Off */}
      {deal.discount_subtype === "DOLLAR_OFF_PURCHASE" && deal.discount_amount_cad && (
        <div className="grid grid-cols-2 gap-3">
          {spend && (
            <div className="bg-muted/50 rounded-lg p-3">
              <p className="text-muted-foreground text-xs mb-1">Min. Purchase</p>
              <p className="font-semibold text-foreground">{formatSpendAmount(spend)}</p>
            </div>
          )}
          <div className="bg-blue-50 dark:bg-blue-950/30 rounded-lg p-3">
            <p className="text-blue-700 dark:text-blue-400 text-xs mb-1">Discount</p>
            <p className="font-bold text-blue-800 dark:text-blue-300 text-lg">{formatDiscountValue(deal.discount_amount_cad)} off</p>
          </div>
        </div>
      )}
      
      {/* Value Summary - with proper formatting */}
      {dollarValue != null && (
        <div className="bg-primary/10 rounded-lg p-3">
          <p className="text-primary text-xs mb-1">Savings</p>
          <p className="font-bold text-primary text-lg">
            {effectivePercent != null ? `≈ ${formatDiscountPercent(effectivePercent)} off` : `≈ ${formatDiscountValue(dollarValue)} off`}
          </p>
          <p className="text-primary/70 text-xs">≈ {formatDiscountValue(dollarValue)} value</p>
        </div>
      )}
    </div>
  );
}

/**
 * Points Layout - Loyalty points with optional cash equivalent
 */
export function PointsDetailLayout({ deal }: DetailLayoutProps) {
  const computedValue = computeDealValueCad(deal);
  const effectivePercent = computeEffectivePercentBack(deal);
  const hasConversion = deal.points_to_currency_rate != null;
  
  return (
    <div className="space-y-3 text-sm">
      <div className="bg-purple-50 dark:bg-purple-950/30 rounded-lg p-3">
        <p className="text-purple-700 dark:text-purple-400 text-xs mb-1">Points Earned</p>
        <p className="font-bold text-purple-800 dark:text-purple-300 text-lg">
          {deal.points_amount?.toLocaleString()} {deal.points_program_name || "points"}
        </p>
      </div>
      
      {hasConversion && computedValue != null ? (
        <div className="bg-primary/10 rounded-lg p-3">
          <p className="text-primary text-xs mb-1">Cash Equivalent</p>
          <p className="font-bold text-primary text-lg">≈ ${computedValue.toFixed(2)} CAD</p>
          {effectivePercent != null && (
            <p className="text-primary/70 text-xs">≈ {Math.round(effectivePercent)}% back</p>
          )}
          <p className="text-muted-foreground text-xs mt-1">
            Rate: {deal.points_to_currency_rate} CAD per point
          </p>
        </div>
      ) : (
        <div className="bg-amber-50 dark:bg-amber-950/30 rounded-lg p-3 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-amber-800 dark:text-amber-300 font-medium">Cash value not available</p>
            <p className="text-amber-700/70 dark:text-amber-400/70 text-xs">
              Conversion rate not defined for this program.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * In-Kind Layout - Free items/tickets with optional valuation
 */
export function InKindDetailLayout({ deal }: DetailLayoutProps) {
  const tiers = deal.in_kind_tiers;
  const tiersFormatted = formatInKindTiers(deal);
  const computedValue = computeDealValueCad(deal);
  const effectivePercent = computeEffectivePercentBack(deal);
  const showConfirmMessage = needsConfirmOnSite(deal);
  
  // Only show tiers section if there are multiple unique spend thresholds
  const hasMultipleTiers = tiers && tiers.length > 1;
  
  return (
    <div className="space-y-3 text-sm">
      {/* Only show tiers if there are multiple thresholds */}
      {hasMultipleTiers && tiersFormatted.length > 1 && (
        <div className="bg-amber-50 dark:bg-amber-950/30 rounded-lg p-3 space-y-2">
          <p className="text-amber-700 dark:text-amber-400 text-xs font-medium">Reward Tiers</p>
          {tiersFormatted.map((tier, idx) => (
            <p key={idx} className="text-amber-800 dark:text-amber-300 font-medium">
              {tier}
            </p>
          ))}
        </div>
      )}
      
      {/* Valuation - only show if available */}
      {!showConfirmMessage && computedValue != null && (
        <div className="bg-primary/10 rounded-lg p-3">
          <p className="text-primary text-xs mb-1">Estimated Value</p>
          <p className="font-bold text-primary text-lg">≈ ${computedValue.toFixed(2)} CAD</p>
          {effectivePercent != null && (
            <p className="text-primary/70 text-xs">≈ {Math.round(effectivePercent)}% back</p>
          )}
          {deal.unit_value_cad && (
            <p className="text-muted-foreground text-xs mt-1">
              Based on ${deal.unit_value_cad}/item (Regular Adult price)
            </p>
          )}
        </div>
      )}
      
      {/* Confirm on site message */}
      {showConfirmMessage && (
        <div className="bg-amber-50 dark:bg-amber-950/30 rounded-lg p-3 flex items-start gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-amber-800 dark:text-amber-300 font-medium">Please confirm on the site</p>
            <p className="text-amber-700/70 dark:text-amber-400/70 text-xs mb-2">
              Value could not be automatically determined.
            </p>
            {(deal.valuation_reference_url || deal.source_url || deal.proof_urls?.[0]) && (
              <a 
                href={deal.valuation_reference_url || deal.source_url || deal.proof_urls?.[0]} 
                target="_blank" 
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-amber-700 hover:text-amber-800 dark:text-amber-400 dark:hover:text-amber-300 underline text-xs"
              >
                View details on merchant site
                <ExternalLink className="w-3 h-3" />
              </a>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Main component that renders the appropriate layout based on deal category
 */
export function DealDetailLayout({ deal }: DetailLayoutProps) {
  const category = inferDealCategory(deal);
  
  switch (category) {
    case "BONUS":
      return <BonusDetailLayout deal={deal} />;
    case "DISCOUNT":
      return <DiscountDetailLayout deal={deal} />;
    case "POINTS":
      return <PointsDetailLayout deal={deal} />;
    case "IN_KIND":
      return <InKindDetailLayout deal={deal} />;
    default:
      return <BonusDetailLayout deal={deal} />;
  }
}